import {
  Rng,
  SLOWMO_RATE,
  TICK_DT,
  TICK_RATE,
  carveCircle,
  createGame,
  drainEvents,
  stepGame,
  type GameState,
  type Intent,
  type MatchStartMsg,
  type SimEvent,
  type SnapshotMsg,
  type Team,
  type WeaponId,
} from '@cuboom/shared';
import type { Connection } from '../net/connection.js';

/** One live aim frame from the seat driving the active character. */
export interface OppAim {
  playerId: string;
  /** Pointer world position (the same value the local renderer aims at). */
  x: number;
  y: number;
  weaponId: WeaponId | null;
  /** Arrival time (ms) and the turn it belongs to. */
  at: number;
  turnIndex: number;
}

const TICK_MS = 1000 / TICK_RATE;
/** Watchdog compare pairs kept from snapshots (≈24 s of 2 Hz heartbeats). */
const WATCH_CAP = 24;
/** Per-tick rng checkpoints kept for the watchdog compare (~4 s). */
const CHECKPOINT_CAP = 240;
/**
 * Snapshots normally lead our lagged copy by ~L (10–60) ticks. A gap beyond
 * this means the server is running while we are not — hopeless behind → recover.
 */
const MAX_LAG_TICKS = 180;
/** Don't spam `request_state`. */
const RESYNC_THROTTLE_MS = 10_000;

/**
 * The client's own copy of the match — deterministic lockstep, the Worms
 * model. Terrain pixels never cross the wire: on match_start we regenerate
 * the identical mask from (seed, theme) and the server's resync path replays
 * its carve log over it.
 *
 * From match start we step `stepGame` locally at 60 Hz exactly like the
 * server does, and the server relays every human intent stamped with the tick
 * it applied it at (`intent_applied`) — we queue it and apply it at that SAME
 * tick. Same seed + same inputs at the same ticks ⇒ identical trajectories,
 * craters, destruction and RNG streams on every machine BY CONSTRUCTION.
 * CPU decisions are never relayed: our own `stepAiming` derives them at the
 * same tick the server does.
 *
 * Clock: the server is the reference. Pong beacons (every ping) and snapshots
 * carry its tick; we extrapolate `serverEst` and steer our tick counter to
 * trail it by `lagTicks ≈ ping/2 + 8` (clamped 10–60) — behind by more than
 * one-way delay, so every stamped input arrives BEFORE we reach its tick —
 * rate-limited to ±[0, 2]× via the accumulator, never skipping or rewinding
 * ticks. Slowmo uses the SAME wall-clock rule as the server's loop (rate ¼),
 * so both clocks slow together and the lag holds.
 *
 * Watchdog: per-tick rng checkpoints vs each snapshot's (tick, rngState), plus
 * a lag-gap guard (a snapshot > 180 ticks ahead means we stopped while the
 * server ran); either logs `[desync]` and requests the full resync
 * (match_start + carveLog replay + snapshot) — recovery, not the normal path.
 *
 * Snapshots do NOT write the sim during normal play (they are AHEAD of our
 * lagged copy); they only feed the clock and the watchdog. Events are never
 * broadcast either — each machine derives its own. The only network FX path
 * left is the resync carve replay (`applyEvents`), and the only network
 * verdict is the p2p `intent_ack`, played instantly for the deny sound.
 */
export class RemoteGame {
  state: GameState | null = null;
  myPlayerId = '';
  mySeat: number | null = null;
  config: MatchStartMsg['config'] | null = null;
  /** The opponent's live aim, relayed by the server while they drive the turn. */
  oppAim: OppAim | null = null;

  /** Stamped inputs awaiting their tick — arrival order = server order. */
  private queue: { intent: Intent; tick: number }[] = [];
  /** Events held while awaiting an adopt (flushed through the filter after). */
  private pending: SimEvent[] = [];
  /** Wall-clock accumulator for sim ticks (60 Hz, slowmo-rated, steered). */
  private acc = 0;
  /** Latest clock beacon: server tick, and when we heard it. */
  private beaconTick = 0;
  private beaconAt = 0;
  /** Ticks we trail the server by (one-way delay + slack), from ping. */
  private lagTicks = 12;
  /** Snapshot rng positions we haven't reached yet (compared when we do). */
  private watch: { tick: number; rngState: number }[] = [];
  /** Our own rng position for the last CHECKPOINT_CAP ticks. */
  private checkpoints = new Map<number, number>();
  /** Adopt the next snapshot wholesale (match_start or an explicit resync). */
  private adoptNext = true;
  /** Throttle anchor — negative so the FIRST request is never throttled. */
  private resyncAt = -RESYNC_THROTTLE_MS;
  /** `[clock]` diagnostic throttle (2 s). */
  private diagAt = -2000;
  /** When the last wholesale adopt landed — in-flight relays drain benignly after it. */
  private lastAdoptAt = -Infinity;

  constructor(private readonly net: Connection) {}

  /** Build the local sim from a match_start (also the resync entry point). */
  applyMatchStart(msg: MatchStartMsg): void {
    this.myPlayerId = msg.yourPlayerId;
    this.config = msg.config;
    this.oppAim = null;
    this.queue = [];
    this.pending = [];
    this.acc = 0;
    this.watch = [];
    this.checkpoints.clear();
    this.adoptNext = true;
    this.resyncAt = -RESYNC_THROTTLE_MS;
    // The fresh match's clock starts at 0; the next beacon/snapshot snaps
    // the estimate to the real server tick.
    this.beaconTick = 0;
    this.beaconAt = performance.now();

    // createGame builds the identical terrain the server did (same seed +
    // config), and gives us entities/teams/seat shapes to hold until the
    // first snapshot overwrites them with authoritative values.
    const g = createGame({ seed: msg.seed, config: msg.config, roster: msg.roster });

    const mine = msg.roster.find((r) => r.playerId === msg.yourPlayerId);
    this.mySeat = mine ? mine.seat : null;
    this.state = g;
  }

  /** Clock beacon from a pong (server's room tick). `now` is test-injectable. */
  onPong(tick: number, now = performance.now()): void {
    if (this.state && tick > 0) this.beacon(tick, now);
  }

  private beacon(tick: number, at = performance.now()): void {
    this.beaconTick = tick;
    this.beaconAt = at;
  }

  /**
   * A human intent the server applied at `tick` — queue it; the local sim
   * applies it at exactly that tick (see `step`). An intent stamped at or
   * before our current tick means our clock ran ahead of the relay: that is a
   * desync, not something to patch around — resync.
   */
  onIntentApplied(msg: { intent: Intent; tick: number }, now = performance.now()): void {
    const g = this.state;
    if (!g) return;
    if (msg.tick <= g.tick) {
      // Right after an adopt this is normal: in-flight relays for ticks the
      // adopted state already includes. Outside that window it means WE
      // stepped past a stamp that never reached the queue — a lost input —
      // and continuing would drift into divergence: recover silently.
      if (now - this.lastAdoptAt < 2000) return;
      console.log('[desync] late stamp', msg.tick, '<=', g.tick, '→ silent adopt');
      this.adoptNext = true;
      return;
    }
    this.queue.push(msg);
    if (this.queue.length > 256) {
      // Pathological backlog — never drop an input silently (a lost stamp is
      // a guaranteed divergence); recover from the next snapshot instead.
      console.log('[desync] intent queue overflow', this.queue.length, '→ silent adopt');
      this.adoptNext = true;
    }
  }

  /**
   * Advance the local sim one render frame at 60 Hz (slowmo-rated, steered
   * against the server clock), applying queued inputs at their stamped ticks,
   * then hand the drained events to `play` — the FX pass, the same frame the
   * sim emitted them (offline parity for EVERYONE's actions; our own moves
   * arrive even sooner through the fork's instant mask).
   */
  step(dt: number, now: number, play: (evs: SimEvent[]) => void): void {
    const g = this.state;
    if (!g || g.phase === 'lobby' || g.phase === 'match_end') return;

    // Trail the server by one-way delay + generous slack: stamped inputs must
    // land BEFORE we reach their tick — a missed stamp is a divergence, so
    // buy real jitter budget (1.5× oneway + 16 ticks ≈ 400 ms at 150 ms RTT).
    // The fork masks the extra trail for our own actions; the opponent's
    // actions were always RTT-bound anyway.
    this.lagTicks = Math.min(
      90,
      Math.max(12, Math.round((this.net.ping / 2 / TICK_MS) * 1.5) + 16),
    );
    // Server tick estimate: beacon + extrapolation at OUR rate (slowmo slows
    // both clocks identically, so the lag survives bullet time).
    const rate = g.slowmo ? 1 / SLOWMO_RATE : 1;
    const est = this.beaconTick + ((now - this.beaconAt) / 1000) * 60 * rate;
    const target = est - this.lagTicks;
    const err = g.tick - target; // >0 = we are ahead of schedule
    // Ahead → slow toward a halt; behind → catch up, capped at 2×.
    const factor = err >= 0 ? Math.max(0, 1 - err * 0.08) : Math.min(2, 1 - err * 0.08);

    // Clock diagnostics (one line / 2 s): enough to see WHICH part lies when
    // the gap guard fires — est, its age, the resulting factor, the lag band.
    if (now - this.diagAt >= 2000) {
      this.diagAt = now;
      console.log('[clock]', {
        tick: g.tick,
        est: Math.round(est),
        gap: Math.round(est - g.tick),
        err: Math.round(err * 10) / 10,
        factor: Math.round(factor * 100) / 100,
        lag: this.lagTicks,
        beaconAge: Math.round(now - this.beaconAt),
        beaconTick: this.beaconTick,
        ping: this.net.ping,
        slowmo: g.slowmo,
        queue: this.queue.length,
      });
    }

    this.acc += dt * factor * rate;
    const evs: SimEvent[] = [];
    let guard = 0;
    while (this.acc >= TICK_DT - 1e-9 && guard++ < 10) {
      this.acc -= TICK_DT;
      const next = g.tick + 1; // stepGame increments first: inputs stamp POST-step
      let intent: Intent | undefined;
      while (this.queue.length && this.queue[0].tick <= next) {
        const q = this.queue.shift()!;
        if (q.tick === next) intent = q.intent;
        else {
          // A stamp whose tick we already stepped past = a lost input. Playing
          // on silently guarantees rng/state divergence from here — recover
          // from the next snapshot (≤500 ms) instead of drifting into a
          // visible desync. No heavy rebuild: the snap alone corrects it.
          console.log('[desync] missed stamp', q.tick, '<=', next, '→ silent adopt');
          this.adoptNext = true;
          break;
        }
      }
      stepGame(g, intent);
      evs.push(...drainEvents(g));
      this.noteCheckpoint(g);
    }

    this.runWatch();
    // While awaiting the adopt snapshot, keep MOTION ticking but HOLD the
    // events — they are flushed (through the fork's echo filter) the frame
    // after the adopt lands: dropping them lost opponent FX mid-recovery,
    // playing them raw during the window double-played post-adopt.
    if (this.adoptNext) {
      this.pending.push(...evs);
      return;
    }
    if (this.pending.length) {
      const batch = this.pending.splice(0).concat(evs);
      play(batch);
      return;
    }
    if (evs.length) play(evs);
  }

  private noteCheckpoint(g: GameState): void {
    this.checkpoints.set(g.tick, g.rng.state);
    while (this.checkpoints.size > CHECKPOINT_CAP) {
      const first = this.checkpoints.keys().next().value as number;
      this.checkpoints.delete(first);
    }
  }

  /** Compare snapshots we've now reached against our own rng history. */
  private runWatch(): void {
    const g = this.state;
    if (!g) return;
    while (this.watch.length && this.watch[0].tick <= g.tick) {
      const w = this.watch.shift()!;
      const cp = this.checkpoints.get(w.tick);
      if (cp !== undefined && cp !== w.rngState) {
        // rng streams parted (a float-boundary branch took differently, or a
        // stamp was lost): the state snap we need is the next snapshot itself
        // — a SILENT adopt, not a full match_start rebuild. Rebuilding the
        // map mid-game was the stutter every divergence cost.
        console.log('[desync] rng at tick', w.tick, 'local', cp, 'server', w.rngState, '→ silent adopt');
        this.adoptNext = true;
        this.watch = [];
        return;
      }
    }
  }

  private requestResync(now: number): void {
    if (now - this.resyncAt < RESYNC_THROTTLE_MS) return;
    this.resyncAt = now;
    this.net.send({ t: 'request_state' });
  }

  /**
   * Snapshot: clock beacon always; watchdog pair normally. On the first
   * snapshot after a match_start/resync (`adoptNext`) the message is the
   * authoritative state and is adopted WHOLESALE — regular snapshots never
   * write the sim (they are ahead of our lagged copy by construction).
   */
  applySnapshot(msg: SnapshotMsg, now = performance.now()): void {
    if (!this.state) return;
    this.beacon(msg.tick, now);
    if (this.adoptNext) {
      this.adoptNext = false;
      this.lastAdoptAt = now;
      const g = this.state;
      const { rngState, ...rest } = msg;
      Object.assign(g, rest);
      if (!(g.rng instanceof Rng)) g.rng = new Rng(rngState);
      else g.rng.state = rngState;
      // Stamps at or before the adopted tick are already baked into this
      // state — drop them. FUTURE stamps are inputs the server has relayed
      // but we haven't reached: clearing those (the old behavior) LOST
      // inputs and caused the very divergence this adopt is correcting.
      this.queue = this.queue.filter((q) => q.tick > g.tick);
      this.watch = [];
      this.checkpoints.clear();
      this.checkpoints.set(g.tick, g.rng.state);
      return;
    }
    // Hopelessly behind: the server is running while we are not (dead clock,
    // wedged loop) — recover instead of drifting further.
    if (msg.tick - this.state.tick > MAX_LAG_TICKS) {
      const est =
        this.beaconTick +
        ((now - this.beaconAt) / 1000) *
          60 *
          (this.state.slowmo ? 1 / SLOWMO_RATE : 1);
      console.log('[desync] behind by', msg.tick - this.state.tick, 'ticks', {
        tick: this.state.tick,
        server: msg.tick,
        est: Math.round(est),
        beaconAge: Math.round(now - this.beaconAt),
        ping: this.net.ping,
      });
      this.requestResync(now);
      return;
    }
    // Watchdog: remember this (tick, rngState) — compared once we reach it.
    this.watch.push({ tick: msg.tick, rngState: msg.rngState });
    if (this.watch.length > WATCH_CAP) this.watch.shift();
  }

  /**
   * The resync carve replay (`sendResync` re-sends match_start → full carve
   * log → snapshot): carve the regenerated mask with the identity-seeded
   * crater rims (same pixels the server carved), and return the events so the
   * render layer redraws them.
   */
  applyEvents(events: SimEvent[]): SimEvent[] {
    const g = this.state;
    if (!g) return events;
    for (const ev of events) {
      if (ev.type === 'terrain_carved') {
        for (const c of ev.circles) carveCircle(g.terrain, c.x, c.y, c.r);
      }
    }
    return events;
  }

  /** Store the opponent's latest aim frame (server-stamped playerId). */
  applyAimState(
    msg: { playerId: string; x: number; y: number; weaponId: WeaponId | null },
    now = performance.now(),
  ): void {
    if (!this.state) return;
    this.oppAim = { ...msg, at: now, turnIndex: this.state.turnIndex };
  }

  /**
   * The opponent's aim for the eyes/barrel — only while it belongs to the
   * current turn and is fresh (~1.2 s: long enough to cover the pointer
   * idling before a drag starts, short enough to relax to facing after).
   */
  aimFor(now: number, maxAgeMs = 1200): OppAim | null {
    const a = this.oppAim;
    if (!a || !this.state || a.turnIndex !== this.state.turnIndex) return null;
    return now - a.at <= maxAgeMs ? a : null;
  }

  /**
   * The weapon the opponent held this turn, usable after they fire: unlike
   * `aimFor` there is no age limit — the character keeps holding what it just
   * shot until the next turn begins (the same persistence local `sel` has).
   */
  heldAim(): OppAim | null {
    const a = this.oppAim;
    if (!a || !this.state || a.turnIndex !== this.state.turnIndex) return null;
    return a;
  }

  /** Send an intent through the connection (server validates seat + turn). */
  submit(intent: Intent): boolean {
    if (!this.net.isOnline) return false;
    this.net.submitIntent(intent);
    return true;
  }

  /** True when the local player owns the active character. */
  get myTurn(): boolean {
    const g = this.state;
    if (!g || this.mySeat == null) return false;
    const active = g.entities.find((e) => e.id === g.activeEntityId);
    return !!active && active.seat === this.mySeat && active.alive && g.phase === 'aiming';
  }

  myTeam(): Team | null {
    const g = this.state;
    if (!g || this.mySeat == null) return null;
    const active = g.entities.find((e) => e.seat === this.mySeat);
    if (!active) return null;
    return g.teams.find((t) => t.id === active.teamId) ?? null;
  }
}
